import React, { useState, useEffect } from 'react';
import UserMenu from './UserMenu';
import FilterSelect from './FilterSelect';
import MultiselectFilter from './MultiselectFilter';
import FilterDatePicker from './FilterDatePicker';
import FilterRangePicker from './FilterRangePicker';
import downloadPageAsPDF, { PDF_EXPORT_CLOSE_DROPDOWNS_EVENT } from '../helpers/downloadPageAsPDF';
import { pageFilterOptionsDefaultState } from '../consts/graphs';
import { FilterOptions, PagesCommonProps } from '../helpers/pagesUtil';

type Props = {
  pageName?: string,
  pdfName?: string,
  hiddenDatepicker?: boolean;
  hiddenRangepicker?: boolean;
  filterOptions: FilterOptions;
  filters: PagesCommonProps["filters"],
  setFilters: PagesCommonProps["setFilters"],
};

const FilterBar: React.FC<Props> = ({
  pdfName,
  filters,
  pageName,
  setFilters,
  filterOptions,
  hiddenDatepicker = true,
  hiddenRangepicker = true,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>();

  useEffect(() => {
    const closeHeaderDropdowns = () => {
      setIsDrawerOpen(false);
      setShowDatePicker(false);
      setShowRangePicker(false);
    };

    window.addEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeHeaderDropdowns);
    return () => window.removeEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeHeaderDropdowns);
  }, []);

  // const userEmail = getUserEmail();
  // const userRole = getUserRole();

  // Handle filter change
  const handleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    filterName: string
  ) => {
    const newFilters = {
      ...filters,
      [filterName]: event.target.value,
    };
    setFilters(newFilters);
  };

  // Handle multiselect filter change
  const handleMultiFilterChange = (values: string[], filterName: string) => {
    const newFilters = {
      ...filters,
      [filterName]: values,
    };
    setFilters(newFilters);
  };

  // Clear filters
  const clearFilters = () => setFilters(pageFilterOptionsDefaultState);

  const getPickerStyle = (selector: "date-icon" | "range-icon"): React.CSSProperties | undefined => {
    const targetElement = document.querySelector(`.${selector}`);
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      return {
        top: "50%",
        left: "50%",
        position: "fixed",
        transform: "translate(-50%, -50%)",
      };
    }

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const isDatePicker = selector === "date-icon";
      const pickerWidth = isDatePicker ? 480 : 320;
      const gutter = 12;
      const preferredLeft = rect.right - pickerWidth;
      const left = Math.min(
        Math.max(gutter, preferredLeft),
        Math.max(gutter, window.innerWidth - pickerWidth - gutter)
      );
      const top = rect.bottom + 8;

      return {
        top,
        left,
        position: "fixed",
      };
    }

    return undefined;
  };

  const openPicker = (selector: "date-icon" | "range-icon") => {
    setPickerStyle(getPickerStyle(selector));
    if (selector === "date-icon") {
      setShowRangePicker(false);
      setShowDatePicker(true);
      return;
    }
    setShowDatePicker(false);
    setShowRangePicker(true);
  };
  const formatFilterLabel = (filterKey: string) => {
    const rawLabel = filterKey;
    if (rawLabel.includes('/')) {
      const parts = rawLabel.split('/').map(part => part.trim()).filter(Boolean);
      return `Select ${parts.join(' And/Or ')}`;
    }
    return `Select ${rawLabel}`;
  };

  const getFilterWidth = (filterKey: string) => {
    if (filterKey.includes('/')) {
      return { minWidth: '220px', maxWidth: '280px' };
    }
    return {};
  };

  const handlePdfExport = async () => {
    if (isExportingPdf) return;

    setIsExportingPdf(true);
    try {
      await downloadPageAsPDF(pdfName || pageName, "pdf-export-target");
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("PDF export failed. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <>
      <div className="filter-bar-v2">
        {/* Drawer Toggle for Mobile */}
        <button
          className="drawer-toggle"
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
        >
          {isDrawerOpen ? "X" : "☰"}
        </button>

        {/* Filter Options for Larger Screens */}
        <div className="filter-options">
          
          <button
            onClick={handlePdfExport}
            disabled={isExportingPdf}
            className="image-button pdf-export-button"
            title={isExportingPdf ? "Exporting PDF..." : "Export as PDF"}
          >
            <span
              className="pdf-export-button__icon"
              style={{
                backgroundImage: `url('${process.env.REACT_APP_PUBLIC_URL || process.env.PUBLIC_URL || ''}/exportPdf.png')`,
                backgroundSize: "20px",
              }}
            />
          </button>
          {Object.keys(filterOptions).map((filterKey, idx) => {
            const isMultiselect = filterKey !== "invertData";
            if (isMultiselect) {
              return (
                <div key={filterKey} className="filter-container">
                  <MultiselectFilter
                    options={filterOptions[filterKey] || []}
                    onApply={(items) => handleMultiFilterChange(items, filterKey)}
                    selectedItems={Array.isArray(filters[filterKey]) ? filters[filterKey] : []}
                    title={formatFilterLabel(filterKey)}
                    placeholder={filterKey}
                    itemLabel={filterKey.toLowerCase()}
                    {...getFilterWidth(filterKey)}
                  />
                </div>
              );
            }
            return (
              <div key={filterKey} className="filter-container">
                <FilterSelect
                  name={filterKey}
                  value={filters[filterKey]}
                  options={filterOptions[filterKey]}
                  onChange={handleFilterChange}
                  noDefault={filterKey === "invertData"}
                />
              </div>
            );
          })}
          {
            !hiddenDatepicker && <button
              className="date-icon"
              children="Date"
              onClick={() => openPicker("date-icon")}
            />
          }
          {
            !hiddenRangepicker && <button
              className="range-icon"
              children="Available Credit"
              onClick={() => openPicker("range-icon")}
            />
          }
          <div className="filter-buttons">
            
            <button style={{ width: 90 }} onClick={clearFilters}>
              Clear
            </button>
          </div>
          <UserMenu lastSyncedDate={filters?.lastSyncedDate} />
        </div>
        
        {/*<div key="00" className="data-sync-text">{userEmail && (
          <span className="user-email" style={{ color: "#08305D", marginRight: 20 }}>
            <b>{userEmail}</b> <i>({
              userRole === "ROLE_COMPANY_ADMIN" ? "Company Admin" : 
              userRole === "ROLE_MANAGER_DASHBOARD_VIEWER" ? "Manager" :
              userRole === "ROLE_SUPER_ADMIN" ? "Super Admin" : "User"
            })</i>
          </span>
        )} {filters?.lastSyncedDate && <span>Updated at: {filters?.lastSyncedDate}</span>}
        </div>*/}
        {/* Drawer for Mobile View */}
        {isDrawerOpen && (
          <div className="mobile-drawer">
           {/*<FilterBarDataInvertSwitch
              invertData={invertData}
              setInvertData={setInvertData}
              hidden={hiddenDataInversion}
            />*/}
            {Object.keys(filterOptions).map((filterKey, idx) => {
              const isMultiselect = filterKey !== "invertData";
              if (isMultiselect) {
                return (
                  <div key={filterKey} className="filter-container">
                    <MultiselectFilter
                      options={filterOptions[filterKey] || []}
                      onApply={(items) => handleMultiFilterChange(items, filterKey)}
                      selectedItems={Array.isArray(filters[filterKey]) ? filters[filterKey] : []}
                      title={formatFilterLabel(filterKey)}
                      placeholder={filterKey}
                      itemLabel={filterKey.toLowerCase()}
                      {...getFilterWidth(filterKey)}
                    />
                  </div>
                );
              }
              return (
                <div key={filterKey} className="filter-container">
                  <FilterSelect
                    name={filterKey}
                    value={filters[filterKey]}
                    options={filterOptions[filterKey]}
                    onChange={handleFilterChange}
                    noDefault={filterKey === "invertData"}
                  />
                </div>
              );
            })}
            {
              !hiddenDatepicker && <button
                className="date-icon"
                children="Select Date 📅"
                onClick={() => openPicker("date-icon")}
              />
            }
            {
              !hiddenRangepicker && <button
                className="range-icon"
                children="Select Available Credit"
                onClick={() => openPicker("range-icon")}
              />
            }
            <button onClick={clearFilters}>Clear</button>
          </div>
        )}
      </div>
      <FilterDatePicker
        variant='ashwin'
        style={pickerStyle}
        isOpen={showDatePicker}
        onClose={() => {
          setShowDatePicker(false);
          setPickerStyle(undefined);
        }}
        onChange={(selection) => setFilters(prevState => ({ ...prevState, ...selection }))}
        minDate={(() => {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setDate(1); // Set to 1st of the month
          return oneYearAgo;
        })()}
        maxDate={new Date()}
        initialRange={
          filters.startDate && filters.endDate 
            ? {
                startDate: new Date(filters.startDate),
                endDate: new Date(filters.endDate),
              }
            : undefined
        }
      />
      <FilterRangePicker
        style={pickerStyle}
        isOpen={showRangePicker}
        labelRange='Available Credits'
        labelPercentageRange='Available Credits'
        onClose={() => {
          setShowRangePicker(false);
          setPickerStyle(undefined);
        }}
        onChange={(selection) => setFilters(prevState => ({ ...prevState, ...selection }))}
        defaultValues={{
          MinRange: filters.MinRange ?? 0,
          MaxRange: filters.MaxRange ?? 20000,
          MinRangePercent: filters.MinRangePercent ?? 0,
          MaxRangePercent: filters.MaxRangePercent ?? 100,
        }}
      />
    </>
  );
};

export default FilterBar;

